/**
 * Arbitrage API Client
 * Cross-exchange arbitrage detection and execution
 */

import { BaseApiClient } from './base-client';
import type { ApiResponse } from './config';

export interface ArbitrageOpportunity {
  id: string;
  symbol: string;
  buy_exchange: string;
  sell_exchange: string;
  buy_price: number;
  sell_price: number;
  spread: number;
  spread_pct: number;
  estimated_profit: number;
  volume_available: number;
  risk_score: number;
  status: 'active' | 'expired' | 'executing' | 'completed';
  expires_at: string;
  detected_at: string;
}

export interface ArbitrageExecution {
  id: string;
  opportunity_id: string;
  symbol: string;
  buy_exchange: string;
  sell_exchange: string;
  buy_order_id: string;
  sell_order_id: string;
  buy_fill_price: number;
  sell_fill_price: number;
  quantity: number;
  gross_profit: number;
  fees: number;
  net_profit: number;
  status: 'pending' | 'partial' | 'completed' | 'failed';
  executed_at: string;
}

export interface ArbitrageSettings {
  min_spread_pct: number;
  max_position_size: number;
  auto_execute: boolean;
  allowed_exchanges: string[];
  allowed_symbols: string[];
  max_concurrent_trades: number;
  slippage_tolerance: number;
}

export interface ExchangeStatus {
  exchange: string;
  connected: boolean;
  latency_ms: number;
  balance: number;
  supported_symbols: string[];
  last_updated: string;
}

export class ArbitrageApiClient extends BaseApiClient {
  // Opportunities
  async getOpportunities(filters?: { symbol?: string; min_spread?: number }): Promise<ApiResponse<ArbitrageOpportunity[]>> {
    const params = new URLSearchParams();
    if (filters?.symbol) params.append('symbol', filters.symbol);
    if (filters?.min_spread) params.append('min_spread', String(filters.min_spread));
    const query = params.toString();
    return this.get(`/api/v1/arbitrage/opportunities${query ? '?' + query : ''}`);
  }

  async getOpportunity(opportunityId: string): Promise<ApiResponse<ArbitrageOpportunity>> {
    return this.get(`/api/v1/arbitrage/opportunities/${opportunityId}`);
  }

  // Execution
  async executeArbitrage(opportunityId: string, quantity?: number): Promise<ApiResponse<ArbitrageExecution>> {
    return this.post(`/api/v1/arbitrage/opportunities/${opportunityId}/execute`, { quantity });
  }

  async getExecutions(limit?: number): Promise<ApiResponse<ArbitrageExecution[]>> {
    const endpoint = limit ? `/api/v1/arbitrage/executions?limit=${limit}` : '/api/v1/arbitrage/executions';
    return this.get(endpoint);
  }

  async getExecution(executionId: string): Promise<ApiResponse<ArbitrageExecution>> {
    return this.get(`/api/v1/arbitrage/executions/${executionId}`);
  }

  // Settings
  async getSettings(): Promise<ApiResponse<ArbitrageSettings>> {
    return this.get('/api/v1/arbitrage/settings');
  }

  async updateSettings(settings: Partial<ArbitrageSettings>): Promise<ApiResponse<ArbitrageSettings>> {
    return this.put('/api/v1/arbitrage/settings', settings);
  }

  // Exchanges
  async getExchangeStatuses(): Promise<ApiResponse<ExchangeStatus[]>> {
    return this.get('/api/v1/arbitrage/exchanges');
  }

  /**
   * @removed CU 86bbcdyjx — do not POST raw credentials.
   * Use appApi.connections create + putCredentials + validate for the exchange venue_id.
   */
  async connectExchange(_exchange: string, _credentials: Record<string, string>): Promise<never> {
    throw new Error(
      'Arbitrage exchange connect with raw credentials removed. Use appApi.connections credentials lifecycle.'
    );
  }

  /**
   * @removed — use appApi.connections.revoke(connection_id).
   */
  async disconnectExchange(_exchange: string): Promise<never> {
    throw new Error('Arbitrage exchange disconnect removed. Use appApi.connections.revoke.');
  }

  // Performance
  async getArbitragePerformance(period?: string): Promise<ApiResponse<{
    total_opportunities: number;
    executed_trades: number;
    total_profit: number;
    avg_spread: number;
    success_rate: number;
    best_trade: ArbitrageExecution | null;
  }>> {
    const endpoint = period ? `/api/v1/arbitrage/performance?period=${period}` : '/api/v1/arbitrage/performance';
    return this.get(endpoint);
  }
}
